import type { ReactNode } from "react";
import { Headphones } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SupportButton } from "./SupportButton";
import { useSupport } from "@/hooks/useSupport";
import { cn } from "@/lib/utils";

export interface HelpCardProps {
  /** Optional override for the headline. */
  title?: ReactNode;
  /** Optional override for the supporting copy. */
  subtitle?: ReactNode;
  /** Optional secondary CTA label override. */
  primaryLabel?: ReactNode;
  /** Pre-filled message handed to the Messenger composer. */
  prefill?: string;
  /** Subject used for the mailto fallback. */
  subject?: string;
  /** Context metadata sent to Intercom alongside `support_chat_opened`. */
  context?: Record<string, unknown>;
  /** Optional Intercom event override (e.g. `trip_help_opened`). */
  eventName?: string;
  /** Visual variant: `surface` is a solid card, `outlined` is a bordered card. */
  variant?: "surface" | "outlined" | "subtle";
  /** `row` (default) places the CTA next to the copy at sm+; `stacked` is always vertical with a full-width CTA, suited to narrow sidebars. */
  layout?: "row" | "stacked";
  /** Optional className passthrough. */
  className?: string;
  /** Optional data-testid for the card root. */
  testId?: string;
}

/**
 * Polished, locale-ready "Need more help?" card used across pages where a
 * dedicated support touchpoint adds value (404, Trip details, Settings,
 * FAQs, etc.). All variants share the same icon, typography hierarchy and
 * CTA so the support surface feels native everywhere it appears.
 */
export function HelpCard({
  title,
  subtitle,
  primaryLabel,
  prefill,
  subject,
  context,
  eventName,
  variant = "surface",
  layout = "row",
  className,
  testId,
}: HelpCardProps) {
  const { t } = useTranslation();
  const { email } = useSupport();

  const containerClasses =
    variant === "subtle"
      ? "bg-muted/40"
      : variant === "outlined"
        ? "border border-border bg-background"
        : "bg-muted/60";

  const isStacked = layout === "stacked";

  return (
    <section
      className={cn(
        "rounded-2xl",
        isStacked ? "p-5" : "p-6 sm:p-8",
        containerClasses,
        className,
      )}
      data-testid={testId || "help-card"}
    >
      <div
        className={cn(
          "flex",
          isStacked
            ? "flex-col items-stretch gap-4"
            : "flex-col items-start gap-5 sm:flex-row sm:items-center sm:justify-between",
        )}
      >
        <div className="flex items-start gap-4">
          <div
            className={cn(
              "flex shrink-0 items-center justify-center rounded-full bg-background shadow-sm ring-1 ring-border",
              isStacked ? "h-10 w-10" : "h-12 w-12",
            )}
            aria-hidden
          >
            <Headphones className={cn(isStacked ? "h-4 w-4" : "h-5 w-5", "text-foreground")} />
          </div>
          <div className="space-y-1">
            <h3 className={cn("font-semibold text-foreground", isStacked ? "text-[15px]" : "text-base sm:text-lg")}>
              {title ?? t("support.card_title", { defaultValue: "Need a hand?" })}
            </h3>
            <p className={cn("text-muted-foreground", isStacked ? "text-[13px] leading-relaxed" : "text-sm")}>
              {subtitle ??
                t("support.card_subtitle", {
                  defaultValue:
                    "Chat with our team. We usually reply within a few minutes.",
                })}
            </p>
            {email && (
              <p className="text-xs text-muted-foreground/80">
                {t("support.or_email", { defaultValue: "Or email us:" })}{" "}
                <a
                  href={`mailto:${email}`}
                  className="font-medium text-foreground underline underline-offset-2 hover:no-underline"
                >
                  {email}
                </a>
              </p>
            )}
          </div>
        </div>
        <div className={isStacked ? "w-full" : "sm:max-w-sm"}>
        <SupportButton
          variant="primary"
          size="md"
          prefill={prefill}
          subject={subject}
          context={context}
          eventName={eventName}
          label={primaryLabel ?? t("support.card_cta", { defaultValue: "Chat with support" })}
          className={isStacked ? "w-full justify-center" : undefined}
          data-testid="help-card-cta"
        />
        </div>
      </div>
    </section>
  );
}
